import { useState, useEffect, useCallback, useRef, type ChangeEvent } from 'react'
import {
  Box,
  Button,
  Card,
  CardBody,
  Heading,
  HStack,
  VStack,
  Text,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Switch,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  Spinner,
  Input,
  useToast,
} from '@chakra-ui/react'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { ActionButtons } from '@/components/common/ActionButtons'
import { apiClient } from '@/lib/api/client'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { exportRowsToCSV } from '@/lib/utils/csvUtils'

const PRIORITY_OPTIONS = [
  { value: 1, label: 'Primary' },
  { value: 2, label: 'Secondary' },
  { value: 3, label: 'Backup' },
]

const SKILL_LEVEL_OPTIONS = [1, 2, 3, 4, 5]

const ROLE_OPTIONS = [
  { value: '', label: 'Select role' },
  { value: 'senior_team_lead', label: 'Senior Team Lead' },
  { value: 'team_lead', label: 'Team Lead' },
  { value: 'service_desk', label: 'Service Desk' },
  { value: 'field_engineer', label: 'Field Engineer' },
]

type ClientOption = { id: number; code: string; name: string }

type AgentRow = {
  user_id: number
  agent_name: string
  user_login: string
  user_email: string
  can_handle: boolean
  priority_level: number | null
  skill_level: number | null
  daily_capacity: number | null
  agent_skill_id?: number
}

type PageData = {
  client: { id: number; code: string; name: string; ticket_source: string | null }
  summary: { total_assigned_agents: number; total_primary_agents: number }
  agents: AgentRow[]
}

export default function AgentSkillsPage() {
  const colors = useThemeColors()
  const toast = useToast()
  const { showError, showSuccess, showWarning } = useToastNotification()
  const [roleFilter, setRoleFilter] = useState<string>('')
  const [clients, setClients] = useState<ClientOption[]>([])
  const [selectedClientId, setSelectedClientId] = useState<string>('')
  const [data, setData] = useState<PageData | null>(null)
  const [loadingClients, setLoadingClients] = useState(true)
  const [loadingData, setLoadingData] = useState(false)
  const [saving, setSaving] = useState(false)
  const [agents, setAgents] = useState<AgentRow[]>([])
  const [dirty, setDirty] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const fetchClients = useCallback(async () => {
    setLoadingClients(true)
    try {
      const res = await apiClient.get<ClientOption[]>('/organization/agent-skills/clients')
      const list = Array.isArray(res.data) ? res.data : []
      setClients(list)
      if (list.length > 0) {
        setSelectedClientId((prev) => (prev ? prev : String(list[0].id)))
      }
    } catch (e) {
      toast({ title: 'Failed to load clients', status: 'error', isClosable: true })
    } finally {
      setLoadingClients(false)
    }
  }, [toast])

  const fetchAgentSkills = useCallback(async () => {
    if (!selectedClientId || !roleFilter) {
      setData(null)
      setAgents([])
      return
    }
    setLoadingData(true)
    try {
      const res = await apiClient.get<PageData>('/organization/agent-skills', {
        params: { client_id: selectedClientId, role: roleFilter },
      })
      setData(res.data)
      setAgents(res.data?.agents ?? [])
      setDirty(false)
    } catch (e) {
      toast({ title: 'Failed to load agent skills', status: 'error', isClosable: true })
      setData(null)
      setAgents([])
    } finally {
      setLoadingData(false)
    }
  }, [selectedClientId, roleFilter, toast])

  useEffect(() => {
    fetchClients()
  }, [])

  useEffect(() => {
    fetchAgentSkills()
  }, [fetchAgentSkills])

  const updateAgent = (userId: number, field: keyof AgentRow, value: unknown) => {
    setAgents((prev) =>
      prev.map((a) =>
        a.user_id === userId
          ? { ...a, [field]: value }
          : a
      )
    )
    setDirty(true)
  }

  const handleBulkSave = async () => {
    if (!selectedClientId || !data) return
    const payload = {
      client_id: Number(selectedClientId),
      skills: agents.map((a) => ({
        user_id: a.user_id,
        can_handle: a.can_handle,
        priority_level: a.can_handle ? a.priority_level ?? null : null,
        skill_level: a.can_handle ? a.skill_level ?? null : null,
        daily_capacity: a.can_handle ? a.daily_capacity ?? null : null,
      })),
    }
    setSaving(true)
    try {
      await apiClient.put('/organization/agent-skills/bulk', payload)
      toast({ title: 'Agent skills saved successfully', status: 'success', isClosable: true })
      setDirty(false)
      fetchAgentSkills()
    } catch (e) {
      toast({ title: 'Failed to save', status: 'error', isClosable: true })
    } finally {
      setSaving(false)
    }
  }

  const noPrimaryWarning = data && data.summary.total_primary_agents === 0 && agents.some((a) => a.can_handle)

  const handleExportData = () => {
    if (!data || !selectedClientId || !roleFilter) {
      showWarning('Export', 'Select a role and load client data before exporting.')
      return
    }
    setIsExporting(true)
    try {
      const roleLabel = ROLE_OPTIONS.find((o) => o.value === roleFilter)?.label ?? roleFilter
      const rows = agents.map((a) => [
        data.client.code,
        data.client.name,
        roleFilter,
        roleLabel,
        a.user_login,
        a.agent_name,
        a.user_email,
        a.can_handle ? 'Yes' : 'No',
        a.priority_level ?? '',
        a.skill_level ?? '',
        a.daily_capacity ?? '',
      ])
      exportRowsToCSV({
        csvHeaders: [
          'Client Code',
          'Client Name',
          'Role Filter',
          'Role',
          'User Login',
          'Agent Name',
          'User Email',
          'Can Handle',
          'Priority Level',
          'Skill Level',
          'Daily Capacity',
        ],
        rows,
        filename: `agent-skills_${data.client.code}_${roleFilter}_${new Date().toISOString().split('T')[0]}.csv`,
        showError,
        showSuccess,
        showWarning,
      })
    } finally {
      setIsExporting(false)
    }
  }

  const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setIsImporting(true)
    try {
      const form = new FormData()
      form.append('file', file)

      const { data: res } = await apiClient.post<{
        success: boolean
        message: string
        data?: { processed: number; errors: string[] }
      }>('/organization/agent-skills/import', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })

      const processed = res?.data?.processed ?? 0
      const errors = res?.data?.errors ?? []
      showSuccess('Success', `Imported/updated ${processed} agent skill row(s)`)
      if (errors.length > 0) {
        showWarning(errors.slice(0, 3).join('; ') + (errors.length > 3 ? '...' : ''))
      }

      fetchAgentSkills()
    } catch (err: any) {
      showError(err)
    } finally {
      setIsImporting(false)
      e.target.value = ''
    }
  }

  return (
    <AdminLayout>
      <Box
        p={6}
        h="100%"
        minH={0}
        minW={0}
        overflow="hidden"
        display="flex"
        flexDirection="column"
      >
        <VStack align="stretch" spacing={4} flex="0 0 auto">
          <HStack justify="space-between" align="flex-start" flexWrap="wrap" spacing={4}>
            <VStack align="start" spacing={1} flex="1" minW="200px">
              <Heading size="lg">Agent Skills Management</Heading>
              <Text color={colors.textSecondary}>
                Select a role and client to view and manage which agents can handle the client.
              </Text>
            </VStack>
            <Box pt={1}>
              <ActionButtons
                pathname="/organization/agent-skills"
                onExport={handleExportData}
                showDownloadTemplate={false}
                isExporting={isExporting}
                isImporting={isImporting}
                fileInputRef={fileInputRef}
                onFileUpload={handleFileUpload}
                acceptFileTypes=".csv"
                hideCreate
              />
            </Box>
          </HStack>

          <HStack align="flex-end" spacing={4} flexWrap="wrap">
            <FormControl maxW="220px">
              <FormLabel>Role</FormLabel>
              <Select
                value={roleFilter}
                onChange={(e) => {
                  setRoleFilter(e.target.value)
                  setDirty(false)
                }}
              >
                {ROLE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl maxW="320px">
              <FormLabel>Client</FormLabel>
              <Select
                placeholder="Select client"
                value={selectedClientId}
                onChange={(e) => {
                  setSelectedClientId(e.target.value)
                  setDirty(false)
                }}
                isDisabled={loadingClients}
              >
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.code})
                  </option>
                ))}
              </Select>
            </FormControl>
          </HStack>
        </VStack>

        {loadingData && (
          <HStack flex="0 0 auto" pt={4}>
            <Spinner size="sm" />
            <Text>Loading agent skills...</Text>
          </HStack>
        )}

        {!loadingData && data && (
          <Box
            flex="1"
            minH={0}
            overflow="hidden"
            display="flex"
            flexDirection="column"
            pt={4}
            gap={4}
          >
            <Card bg={colors.cardBg} flex="0 0 auto">
              <CardBody>
                <Heading size="sm" mb={3}>
                  {data.client.name}
                </Heading>
                <HStack spacing={6} flexWrap="wrap">
                  <Text>
                    <Text as="span" fontWeight="medium">Ticket source:</Text>{' '}
                    {data.client.ticket_source ?? '—'}
                  </Text>
                  <Text>
                    <Text as="span" fontWeight="medium">Total assigned agents:</Text>{' '}
                    {data.summary.total_assigned_agents}
                  </Text>
                  <Text>
                    <Text as="span" fontWeight="medium">Total primary agents:</Text>{' '}
                    {data.summary.total_primary_agents}
                  </Text>
                </HStack>
              </CardBody>
            </Card>

            {noPrimaryWarning && (
              <Alert status="warning" flex="0 0 auto">
                <AlertIcon />
                No primary agent is set for this client. Consider setting at least one agent as Primary.
              </Alert>
            )}

            <TableContainer
              borderWidth="1px"
              borderRadius="md"
              flex="1"
              minH={0}
              maxH="55vh"
              overflowY="auto"
              overflowX="auto"
            >
              <Table size="sm">
                  <Thead bg={colors.cardBg}>
                    <Tr>
                      <Th>Agent Name</Th>
                      <Th>Can Handle</Th>
                      <Th>Priority</Th>
                      <Th>Skill Level</Th>
                      <Th>Daily Capacity</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {agents.map((agent) => (
                      <Tr key={agent.user_id}>
                        <Td>
                          <Text fontWeight="medium">{agent.agent_name}</Text>
                          <Text fontSize="xs" color={colors.textSecondary}>
                            {agent.user_login} · {agent.user_email}
                          </Text>
                        </Td>
                        <Td>
                          <Switch
                            isChecked={agent.can_handle}
                            onChange={(e) =>
                              updateAgent(agent.user_id, 'can_handle', e.target.checked)
                            }
                          />
                        </Td>
                        <Td>
                          <Select
                            size="sm"
                            maxW="140px"
                            value={agent.priority_level ?? ''}
                            isDisabled={!agent.can_handle}
                            onChange={(e) =>
                              updateAgent(
                                agent.user_id,
                                'priority_level',
                                e.target.value ? Number(e.target.value) : null
                              )
                            }
                          >
                            <option value="">—</option>
                            {PRIORITY_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </Select>
                        </Td>
                        <Td>
                          <Select
                            size="sm"
                            maxW="80px"
                            value={agent.skill_level ?? ''}
                            isDisabled={!agent.can_handle}
                            onChange={(e) =>
                              updateAgent(
                                agent.user_id,
                                'skill_level',
                                e.target.value ? Number(e.target.value) : null
                              )
                            }
                          >
                            <option value="">—</option>
                            {SKILL_LEVEL_OPTIONS.map((n) => (
                              <option key={n} value={n}>
                                {n}
                              </option>
                            ))}
                          </Select>
                        </Td>
                        <Td>
                          <Input
                            type="number"
                            min={0}
                            maxW="80px"
                            size="sm"
                            value={agent.daily_capacity ?? ''}
                            isDisabled={!agent.can_handle}
                            onChange={(e) =>
                              updateAgent(
                                agent.user_id,
                                'daily_capacity',
                                e.target.value === '' ? null : Number(e.target.value)
                              )
                            }
                          />
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
            </TableContainer>

            <HStack flex="0 0 auto">
              <Button
                colorScheme="blue"
                onClick={handleBulkSave}
                isDisabled={!dirty || saving}
                isLoading={saving}
              >
                Save changes
              </Button>
              {dirty && (
                <Text color={colors.textSecondary} fontSize="sm">
                  You have unsaved changes.
                </Text>
              )}
            </HStack>
          </Box>
        )}

        {!loadingData && selectedClientId && !roleFilter && (
          <Text pt={4} flex="0 0 auto" color={colors.textSecondary}>
            Select a role to view and manage agents for the selected client.
          </Text>
        )}

        {!loadingData && selectedClientId && roleFilter && data && agents.length === 0 && (
          <Text pt={4} flex="0 0 auto">No agents found for the selected role and client.</Text>
        )}
      </Box>
    </AdminLayout>
  )
}
